import { useMemo, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2,
  Typography
} from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'
import { useAuth } from 'components/AuthProvider'
import CustomTooltip from 'components/Tooltip/Tooltip'
import DefaultLayout from 'components/layout/DefaultLayout'
import Button from 'components/Button'
import BankAccountCard from 'components/BankAccountCard'
import { addBankAccountFormPath, depositFundsPath } from 'components/Routes'
import {
  useBankAccounts,
  useMutateActivateBankAccount,
  useMutateArchiveBankAccount
} from 'hooks/useCompliance'
import { useStyles } from './styles'
import { ProfileBankAccountItem, ProfileBankAccountStatus } from './types'

export default function BankAccount(): JSX.Element {
  const { classes } = useStyles()
  const { loading: authLoading, setToast, refreshCurrentUser } = useAuth()
  const navigate = useNavigate()
  const [loadingArchiveId, setLoadingArchiveId] = useState<number | null>(null)
  const [loadingSetActiveId, setLoadingSetActiveId] = useState<number | null>(
    null
  )
  const [removeConfirmId, setRemoveConfirmId] = useState<number | null>(null)

  const { data: bankAccountsData, isLoading: accountsLoading } =
    useBankAccounts()

  const archiveBankAccountMutation = useMutateArchiveBankAccount()
  const activateBankAccountMutation = useMutateActivateBankAccount()

  const accounts = useMemo<ProfileBankAccountItem[]>(() => {
    return (bankAccountsData ?? []).map(account => ({
      id: String(account.id),
      accountNumber: account.account_number ?? null,
      accountName: account.account_name ?? null,
      active: account.active,
      verified: account.verified,
      comment: null,
      status: account.status as ProfileBankAccountStatus
    }))
  }, [bankAccountsData])

  const hasApprovedAccounts = useMemo(
    () => accounts.length > 0 && accounts.every(a => a.status === 'approved'),
    [accounts]
  )

  const hasPendingOrRejected = useMemo(
    () =>
      accounts.some(a => a.status === 'processing' || a.status === 'rejected'),
    [accounts]
  )

  const { activeAccounts, otherAccounts } = useMemo(() => {
    const active = accounts.filter(a => !!a?.active)
    const other = accounts.filter(a => !a?.active)
    return { activeAccounts: active, otherAccounts: other }
  }, [accounts])

  const handleRemoveClick = useCallback((id: number) => {
    setRemoveConfirmId(id)
  }, [])

  const handleRemoveConfirm = useCallback(() => {
    if (removeConfirmId === null) return
    setLoadingArchiveId(removeConfirmId)
    archiveBankAccountMutation.mutate(removeConfirmId, {
      onSuccess: () => {
        setLoadingArchiveId(null)
        setToast({
          open: true,
          message: 'Bank account removed.',
          type: 'success',
          duration: 3000
        })
      },
      onError: (err: unknown) => {
        setLoadingArchiveId(null)
        setToast({
          open: true,
          message:
            err instanceof Error
              ? err.message
              : 'Failed to remove bank account',
          type: 'error',
          duration: 5000
        })
      }
    })
    setRemoveConfirmId(null)
  }, [removeConfirmId, archiveBankAccountMutation, setToast])

  const handleRemoveCancel = useCallback(() => {
    setRemoveConfirmId(null)
  }, [])

  const handleSetActive = useCallback(
    (id: number) => {
      setLoadingSetActiveId(id)
      activateBankAccountMutation.mutate(id, {
        onSuccess: () => {
          setLoadingSetActiveId(null)
          void refreshCurrentUser()
          setToast({
            open: true,
            message: 'Bank account set as active.',
            type: 'success',
            duration: 3000
          })
        },
        onError: (err: unknown) => {
          setLoadingSetActiveId(null)
          setToast({
            open: true,
            message:
              err instanceof Error
                ? err.message
                : 'Failed to set active bank account',
            type: 'error',
            duration: 5000
          })
        }
      })
    },
    [activateBankAccountMutation, refreshCurrentUser, setToast]
  )

  const hasNoActivatedAccount = activeAccounts.length === 0

  const renderHeading = () => (
    <Box className={classes.heading}>
      <Typography variant='h1'>Bank account</Typography>
      <CustomTooltip
        title={
          <>
            Keeping your money safe is important to us. Withdrawals can only be
            paid to a verified bank account in your name. Add, remove or change
            your active withdrawal account. Changes to your bank accounts are
            subject to review to help keep your investment safe in case someone
            else accesses your account.
          </>
        }
        Icon={<HelpIcon fontSize='medium' color='primary' />}
        placement='bottom'
      />
    </Box>
  )

  const loading = authLoading || accountsLoading

  const accountToRemove =
    removeConfirmId != null
      ? accounts.find(a => parseInt(a.id, 10) === removeConfirmId)
      : null

  return (
    <DefaultLayout
      wrapperContent={{ mt: 0, mb: 2 }}
      heading={renderHeading()}
      showBackIcon
      loading={loading}
      loadingAnimation
    >
      <Dialog
        open={removeConfirmId !== null}
        onClose={handleRemoveCancel}
        aria-labelledby='delete-bank-account-dialog-title'
        aria-describedby='delete-bank-account-dialog-description'
      >
        <DialogTitle id='delete-bank-account-dialog-title'>
          Are you sure you want to delete this bank account?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-bank-account-dialog-description'>
            {accountToRemove
              ? `You will not be able to withdraw from this bank account "${accountToRemove.accountNumber}" once deleted. You can always add it again later.`
              : 'You will not be able to withdraw from this bank account once deleted. You can always add it again later.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={handleRemoveCancel}>
            No, Go back
          </Button>
          <Button
            color='primary'
            variant='contained'
            onClick={handleRemoveConfirm}
            disabled={loadingArchiveId !== null}
            loading={loadingArchiveId === removeConfirmId}
            autoFocus
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Grid2 container spacing={2}>
        {hasNoActivatedAccount && !loading && accounts.length > 0 && (
          <Grid2 size={{ xs: 12 }}>
            <Typography variant='body1' paragraph>
              You don't have an active bank account for withdrawals. You will be
              notified once your bank account is approved and active.
            </Typography>
          </Grid2>
        )}

        {accounts.length === 0 && !loading ? (
          <Grid2 size={{ xs: 12 }}>
            <Box className={classes.emptyState}>
              <Typography variant='body1' paragraph>
                We can only process withdrawals to your verified bank account.
              </Typography>
              <Typography variant='body1' paragraph>
                To add a bank account, you can:
              </Typography>
              <ul className={classes.emptyStateList}>
                <li className={classes.emptyStateListItem}>
                  <Typography variant='body1' component='span'>
                    Make a deposit into your Goldie account via instant or
                    manual bank transfer, or
                  </Typography>
                </li>
                <li className={classes.emptyStateListItem}>
                  <Typography variant='body1' component='span'>
                    Upload a recent bank statement (showing your name and
                    account number) for manual verification.
                  </Typography>
                </li>
              </ul>
              <Typography variant='body1'>
                Deposits and withdrawals are usually processed within 1-3
                business days.
              </Typography>

              <Box className={classes.emptyStateButtonGroup}>
                <Button
                  color='primary'
                  fullWidth
                  onClick={() => navigate(depositFundsPath())}
                >
                  Make a deposit
                </Button>
                <Button
                  color='primary'
                  fullWidth
                  variant='outlined'
                  onClick={() => navigate(addBankAccountFormPath())}
                >
                  Add bank account
                </Button>
              </Box>
            </Box>
          </Grid2>
        ) : (
          <>
            {activeAccounts.length > 0 && (
              <Grid2 size={{ xs: 12 }}>
                <Typography variant='body1' paragraph>
                  This is the account we'll use whenever you make a withdrawal
                  from your Goldie vault.
                </Typography>
                {activeAccounts.map(account => (
                  <Box key={account.id} sx={{ mb: 2 }}>
                    <BankAccountCard
                      account={account}
                      onSetActive={handleSetActive}
                      onRemove={handleRemoveClick}
                      loadingArchiveId={loadingArchiveId}
                      loadingSetActiveId={loadingSetActiveId}
                    />
                  </Box>
                ))}
              </Grid2>
            )}
            {otherAccounts.length > 0 && (
              <>
                <Grid2 size={{ xs: 12 }}>
                  <Typography variant='h6' color='textPrimary'>
                    Other bank accounts
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  {otherAccounts.map(account => (
                    <Box key={account.id} sx={{ mb: 2 }}>
                      <BankAccountCard
                        account={account}
                        onSetActive={handleSetActive}
                        onRemove={handleRemoveClick}
                        loadingArchiveId={loadingArchiveId}
                        loadingSetActiveId={loadingSetActiveId}
                      />
                    </Box>
                  ))}
                </Grid2>
              </>
            )}
          </>
        )}
        {(hasApprovedAccounts || hasPendingOrRejected) && (
          <Grid2 size={{ xs: 12 }}>
            {hasPendingOrRejected && (
              <Typography variant='body2' color='textSecondary' mb={2}>
                You cannot add a new bank account while a pending or rejected
                account exists. Remove that account before adding a different
                one.
              </Typography>
            )}
            <Button
              color='primary'
              fullWidth
              disabled={hasPendingOrRejected}
              onClick={() => navigate(addBankAccountFormPath())}
            >
              Add another bank account
            </Button>
          </Grid2>
        )}
      </Grid2>
    </DefaultLayout>
  )
}
